import { ConnectionPositionPair } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { takeUntil } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import {
  DialogService,
  DialogWidth,
  IconName,
  IconComponent,
  IconColorDirective,
  DropdownItemComponent,
  DropdownComponent,
  RtTooltip,
  OverlayOriginDirective,
  ConnectedOverlayDirective,
} from '@recruitee/user-interface';

import { WysiwygInlineButtonComponent } from '../../wysiwyg-inline-button/wysiwyg-inline-button.component';
import { WysiwygAction, WysiwygEmbeddableComponent } from '../adapter/wysiwyg-adapter.types';
import { RedactorInsertsDialogComponent } from './redactor-inserts-dialog/redactor-inserts-dialog.component';
import {
  LinkActionType,
  WysiwygLink,
  WysiwygLinkAction,
  WysiwygLinkType,
} from './wysiwyg-link.types';

@Component({
  selector: 'rt-wysiwyg-link',
  templateUrl: './wysiwyg-link.component.html',
  styleUrls: ['./wysiwyg-link.component.less'],
  imports: [
    WysiwygInlineButtonComponent,
    OverlayOriginDirective,
    RtTooltip,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconColorDirective,
    IconComponent,
    TranslatePipe,
  ],
})
export class WysiwygLinkComponent
  extends WysiwygEmbeddableComponent<WysiwygLink, WysiwygLinkAction>
  implements OnInit
{
  public readonly icons: { [key in WysiwygLinkType]?: IconName } = {
    [WysiwygLinkType.Scheduler]: 'access-time',
    [WysiwygLinkType.GdprChange]: 'gdpr-candidate',
    [WysiwygLinkType.GdprConsentRequest]: 'gdpr-candidate',
    [WysiwygLinkType.GdprRemove]: 'gdpr-candidate',
    [WysiwygLinkType.Questionnaires]: 'list',
    [WysiwygLinkType.IntegrationsKununuApplicantFeedback]: 'rti-kununu',
    [WysiwygLinkType.IntegrationsKununuEmployeeFeedback]: 'rti-kununu',
  };

  @Output() public closeDropdown: EventEmitter<void> = new EventEmitter();

  public dropdownOpened: boolean = false;
  public noEdit: boolean | undefined = false;

  public dropdownPositions: ConnectionPositionPair[] = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'bottom' },
      { overlayX: 'start', overlayY: 'top' },
    ),
    new ConnectionPositionPair(
      { originX: 'start', originY: 'top' },
      { overlayX: 'start', overlayY: 'bottom' },
    ),
  ];

  public texts: {
    dropdownTitle: string | undefined;
    dialogHeading: string | undefined;
    linkName: string | undefined;
    label?: string;
    isValidLink: boolean;
  } = {
    dropdownTitle: undefined,
    dialogHeading: undefined,
    linkName: undefined,
    isValidLink: false,
  };

  constructor(
    private dialogService: DialogService,
    private vcRef: ViewContainerRef,
    protected injector: Injector,
    protected cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {
    super(injector);
  }

  public edit(link: WysiwygLink): void {
    this.openModal(link);
  }

  public openModal(link: WysiwygLink) {
    const dialog = this.dialogService.openCustom(
      RedactorInsertsDialogComponent,
      { width: DialogWidth.Small },
      {
        heading: this.texts.dialogHeading,
        type: link.type!,
        link: link.url!,
        label: link.label!,
        defaultLabel: link.defaultLabel,
        isEdit: true,
        expirationDays: +link.id!,
      },
      this.vcRef,
    );
    dialog.afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(opts => {
      if (opts) {
        if (opts.delete) {
          this.emit(new WysiwygAction(LinkActionType.LinkDelete, link));
        } else {
          this.emit(
            new WysiwygAction(LinkActionType.LinkEdit, {
              ...link,
              url: opts.link,
              label: opts.label
                ? opts.label
                : link.type !== WysiwygLinkType.Scheduler &&
                    link.type !== WysiwygLinkType.Questionnaires
                  ? link.name
                  : opts.link,
            }),
          );
        }
      }
      this.closeDropdown.emit();
    });
  }

  public remove(link: WysiwygLink): void {
    this.emit(new WysiwygAction(LinkActionType.LinkDelete, link));
  }

  public isPlaceholder(link: string | undefined): boolean {
    if (link) return /^\[.*]/.test(link);
    return false;
  }
  // eslint-disable-next-line max-lines-per-function
  protected onStateChange(nextState: WysiwygLink, prevState: WysiwygLink): void {
    this.noEdit = nextState?.noEdit;
    // eslint-disable-next-line default-case
    switch (nextState.type) {
      case WysiwygLinkType.Scheduler:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.scheduler'),
          dialogHeading: this.i18n.translate('app.redactor_inserts.scheduler.dialog.heading', {
            scheduler_name: nextState.name,
          }),
          linkName: this.i18n.translate('app.wysiwyg.link.scheduler_link_name'),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.Questionnaires:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.questionnaires'),
          dialogHeading: this.i18n.translate('app.redactor_inserts.questionnaires.dialog.heading', {
            questionnaire_name: nextState.name,
          }),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.GdprConsentRequest:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          dialogHeading: this.i18n.translate(
            'app.gdpr.inserts_dialog.consent_request.edit_heading',
          ),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
      case WysiwygLinkType.GdprChange:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          dialogHeading: this.i18n.translate('app.gdpr.inserts_dialog.change.edit_heading'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
      case WysiwygLinkType.GdprRemove:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.gdpr'),
          dialogHeading: this.i18n.translate('app.gdpr.inserts_dialog.removal.edit_heading'),
          linkName: nextState.label,
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.IntegrationsKununuApplicantFeedback:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.kununu'),
          dialogHeading: undefined,
          linkName: this.i18n.translate(
            'app.wysiwyg.link.integrations_kununu_applicant_feedback_link_name',
          ),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;

      case WysiwygLinkType.IntegrationsKununuEmployeeFeedback:
        this.texts = {
          dropdownTitle: this.i18n.translate('app.wysiwyg.context_menu.kununu'),
          dialogHeading: undefined,
          linkName: this.i18n.translate(
            'app.wysiwyg.link.integrations_kununu_employee_feedback_link_name',
          ),
          label: nextState.label,
          isValidLink: !this.isPlaceholder(nextState.url),
        };
        break;
    }

    super.onStateChange(nextState, prevState);
  }
}
